<!-- eslint-disable vue/no-mutating-props -->
<template>
  <transition name="modal-fade">
    <div class="modal-backdrop">
      <v-dialog
        max-width="700"
        v-model="dialog"
        content-class="sc-modal sc-modal--lg"
        scrollable
      >
        <template v-slot:activator="{ on, attrs }">
          <template v-if="isOnAdmin">
            <div class="table-action-box" v-bind="attrs" v-on="on">
              <v-icon small>mdi-pencil</v-icon>
              <span class="table-action-label">Editar</span>
            </div>
          </template>
          <template v-else>
            <v-tooltip left color="surface_neutral_selected">
              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <div
                  class="card-action-box card-action-box--edit"
                  role="button"
                  aria-label="Editar câmera"
                  v-bind="{ ...attrs, ...tooltipAttrs }"
                  v-on="{ ...on, ...tooltipOn }"
                >
                  <v-icon>mdi-pencil</v-icon>
                </div>
              </template>
              <span>Editar câmera</span>
            </v-tooltip>
          </template>
        </template>

        <v-card v-if="dialog" flat class="sc-modal__card" :key="cameraData.id">
          <!-- Close button -->
          <v-btn icon class="sc-modal__close" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <!-- Hero Header -->
          <div class="hero-header">
            <div class="hero-cover">
              <v-img
                v-if="cameraData.thumbnail_url"
                :src="cameraData.thumbnail_url"
                alt="Thumb câmera"
                height="100%"
              />
              <div v-else class="hero-cover__empty">
                <v-icon size="40" color="text_secondary">mdi-video-outline</v-icon>
              </div>
            </div>

            <div class="hero-content">
              <!-- Inline name edit -->
              <div class="inline-edit-container">
                <transition name="edit-transition" mode="out-in" @after-enter="onNameTransitionComplete">
                  <div
                    v-if="!isEditingName"
                    key="display"
                    class="name-display"
                    @click="startEditingName"
                  >
                    <h2 class="hero-name">{{ nome || 'Nome da câmera' }}</h2>
                    <v-icon small class="name-edit-icon">mdi-pencil</v-icon>
                  </div>
                  <div v-else key="edit" class="name-edit">
                    <div class="name-input-wrapper">
                      <input
                        ref="nameInput"
                        v-model="nome"
                        class="name-input"
                        placeholder="Nome da câmera"
                        @keydown.enter="finishEditingName"
                        @keydown.esc="cancelEditingName"
                        @blur="onNameBlur"
                      />
                      <div class="name-input-border"></div>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Meta chips -->
              <div class="hero-meta-row">
                <span v-if="cameraData.praca" class="hero-meta-chip">
                  <v-icon x-small>mdi-map-marker</v-icon>
                  {{ cameraData.praca.sig_praca }}
                </span>
                <span v-if="showStatus && cameraData.status" class="hero-meta-chip">
                  <v-icon x-small color="red">mdi-circle</v-icon>
                  {{ cameraData.status }}
                </span>
                <span v-if="cameraData.created_at" class="hero-meta-chip">
                  <v-icon x-small>mdi-clock-outline</v-icon>
                  {{ formatDate(cameraData.created_at) }} · {{ formatHour(cameraData.created_at) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Form fields -->
          <div class="edit-body">
            <div class="field">
              <label class="field-label">DESCRIÇÃO</label>
              <v-text-field
                v-model="descricao"
                solo flat dense hide-details="auto"
                background-color="surface_tertiary"
                placeholder="Descrição da câmera"
              />
            </div>

            <div class="field">
              <label class="field-label">ENDEREÇO</label>
              <v-text-field
                v-model="endereco"
                solo flat dense hide-details="auto"
                background-color="surface_tertiary"
                placeholder="Endereço da câmera"
              />
            </div>

            <div class="field-row">
              <div class="field field--grow">
                <label class="field-label">
                  <v-icon x-small color="text_secondary" class="mr-1">mdi-access-point</v-icon>
                  URL RTSP
                </label>
                <v-text-field
                  v-model="source_url"
                  solo flat dense hide-details="auto"
                  background-color="surface_tertiary"
                  placeholder="rtsp://..."
                />
              </div>
              <div class="field field--geo">
                <label class="field-label">
                  <v-icon x-small color="text_secondary" class="mr-1">mdi-map-marker-outline</v-icon>
                  GEOLOCALIZAÇÃO
                </label>
                <v-text-field
                  v-model="geolocation"
                  solo flat dense hide-details="auto"
                  background-color="surface_tertiary"
                  placeholder="lat, lng"
                />
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="sc-modal__footer">
            <v-btn text class="modal-btn-cancel" @click="dialog = false">Cancelar</v-btn>
            <v-btn color="primary" class="modal-btn-confirm" :loading="loading" @click.prevent="handleUpdateCamera">
              <v-icon small class="mr-1">mdi-check</v-icon>
              Salvar
            </v-btn>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </transition>
</template>

<script>
import { apiClient } from "@/services/api";
import { formatDate, formatHour } from "@/utils/dateUtils";
import { mapActions } from "vuex";

export default {
  props: {
    cameraData: Object,
    isOnAdmin: {
      default: false,
      type: Boolean,
    },
    showStatus: {
      default: false,
      type: Boolean,
    },

  },
  data() {
    return {
      dialog: false,
      loading: false,
      isEditingName: false,
      originalName: "",
      nome: "",
      descricao: "",
      endereco: "",
      coordenadas: {},
      source_url: "",
    };
  },
  watch: {
    dialog(val) {
      if (val) {
        this.nome = this.cameraData.nome || "";
        this.descricao = this.cameraData.descricao || "";
        this.endereco = this.cameraData.endereco || "";
        this.coordenadas = {
          latitude: this.cameraData.latitude,
          longitude: this.cameraData.longitude,
        };
        this.source_url = this.cameraData.rtsp_source || "";
        this.isEditingName = false;
      }
    },
  },
  methods: {
    ...mapActions({
      getCamerasByPraca: "cameras/getCamerasByPraca",
      getCamerasProducao: "cameras/getCameras",
    }),
    formatDate,
    formatHour,
    startEditingName() {
      this.originalName = this.nome;
      this.isEditingName = true;
    },
    onNameTransitionComplete() {
      if (this.isEditingName && this.$refs.nameInput) {
        this.$refs.nameInput.focus();
      }
    },
    finishEditingName() {
      if ((this.nome || "").trim() === "") {
        this.nome = this.originalName;
      }
      this.isEditingName = false;
    },
    cancelEditingName() {
      this.nome = this.originalName;
      this.isEditingName = false;
    },
    onNameBlur() {
      setTimeout(() => {
        if (this.isEditingName) {
          this.finishEditingName();
        }
      }, 150);
    },
    async handleUpdateCamera() {
      if ((this.nome || "").trim() === "") {
        this.$toast.error("Informe o nome da câmera.");
        return;
      }
      this.loading = true;
      try {
        await apiClient.put(`cameras/${this.cameraData.id}`, {
          nome: this.nome,
          descricao: this.descricao,
          endereco: this.endereco,
          coordenadas: this.coordenadas,
          rtsp_source: this.source_url,
        });
        if (this.isOnAdmin) {
          await this.getCamerasByPraca({
            page: this.$store.state.cameras.camerasPraca.page,
            size: this.$store.state.cameras.camerasPraca.size,
            cod_praca: this.cameraData.cod_praca,
            filtro_texto: "",
          });
        } else {
          await this.getCamerasProducao({
            cod_praca: this.$store.state.cameras.selectedPraca.cod_praca,
            cod_producao: this.$store.state.producoes.selectedProducao.cod_producao,
            filtro_texto: this.$store.state.cameras.filtroCameras,
            flg_favorita: this.$store.state.cameras.flg_favorita,
            page: this.$store.state.cameras.camerasProducao.currentPage,
            size: 5,
          });
        }
        this.$toast.success("Câmera atualizada com sucesso!");
        this.dialog = false;
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao atualizar a câmera.";
        this.$toast.error(msg);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    geolocation: {
      get() {
        if (!this.coordenadas.latitude && !this.coordenadas.longitude) {
          return "";
        }
        return `${this.coordenadas.latitude || ""}, ${this.coordenadas.longitude || ""}`;
      },
      set(newValue) {
        const parts = (newValue || "").split(",");
        this.coordenadas.latitude = (parts[0] || "").trim();
        this.coordenadas.longitude = (parts[1] || "").trim();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
/* Hero Header */
.hero-header {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, var(--v-surface_tertiary-base) 0%, var(--v-surface_secondary-base) 100%);
  border-bottom: 1px solid var(--sc-border-subtle);
}

.hero-cover {
  flex-shrink: 0;
  width: 160px;
  height: 120px;
  border-radius: var(--sc-radius-md);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);

  &__empty {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--v-surface_tertiary-base);
    border: 1px dashed var(--sc-border-default);
    border-radius: var(--sc-radius-md);
  }
}

.hero-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding-right: 40px;
}

.hero-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.hero-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: var(--v-text_secondary-base);
  background: var(--sc-surface-tint-hover);
  padding: 3px 8px;
  border-radius: var(--sc-radius-sm);
  border: 1px solid var(--sc-border-subtle);

  .v-icon { opacity: 0.6; }
}

/* Inline name editor */
.inline-edit-container {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  margin-bottom: 8px;
}

.name-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: var(--sc-radius-md);
  transition: all 0.25s var(--sc-easing-standard);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: var(--sc-radius-md);
    background: rgba(255, 255, 255, 0);
    transition: background 0.25s var(--sc-easing-standard);
  }

  &:hover {
    &::before { background: var(--sc-surface-tint-hover); }
    .name-edit-icon { opacity: 0.8; transform: scale(1); }
  }

  &:active { transform: scale(0.98); }
}

.hero-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  margin: 0;
  line-height: 1.4;
  position: relative;
  z-index: 1;
}

.name-edit-icon {
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.25s var(--sc-easing-standard);
  color: var(--v-text_secondary-base);
  position: relative;
  z-index: 1;
}

.name-edit {
  display: inline-flex;
  align-items: center;
  width: 100%;
}

.name-input-wrapper {
  position: relative;
  width: 100%;
}

.name-input {
  background: var(--sc-surface-tint);
  border: none;
  border-radius: var(--sc-radius-md);
  padding: 8px 14px;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  outline: none;
  font-family: inherit;
  position: relative;
  z-index: 1;
  width: 100%;
}

.name-input-border {
  position: absolute;
  inset: 0;
  border: 2px solid var(--v-primary-base);
  border-radius: var(--sc-radius-md);
  pointer-events: none;
}

/* Edit transition */
.edit-transition-enter-active {
  animation: edit-in 0.25s var(--sc-easing-standard);
}
.edit-transition-leave-active {
  animation: edit-out 0.2s var(--sc-easing-standard);
}
@keyframes edit-in {
  0% { opacity: 0; transform: translateY(-8px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes edit-out {
  0% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(8px) scale(0.96); }
}

/* Form body */
.edit-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--v-text_secondary-base);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.field-row {
  display: flex;
  gap: 16px;
}

.field--grow {
  flex: 1;
}

.field--geo {
  width: 200px;
  flex-shrink: 0;
}

/* Modal fade */
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease; }
</style>
